import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Download, Loader2, RefreshCw, Building2, BarChart3,
  TrendingUp, Users, ShoppingCart, Shield, Calendar
} from 'lucide-react';
import { authAPI, isPlatformAdmin } from '../lib/auth';

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const PERIOD_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
];

const SCAN_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'classification', label: 'Onboarding' },
  { value: 'monitoring', label: 'Monitoring' },
];

function StatCard({ icon: Icon, label, value, sub, color }) {
  const colors = {
    blue:   'bg-blue-50 border-blue-200 text-blue-700',
    green:  'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    teal:   'bg-teal-50 border-teal-200 text-teal-700',
  };
  return (
    <div className={`rounded-xl border p-4 flex items-start gap-3 ${colors[color] || colors.blue}`}>
      <div className="p-2 rounded-lg bg-white bg-opacity-60"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-2xl font-bold">{value}</div>
        <div className="text-sm font-medium opacity-80">{label}</div>
        {sub && <div className="text-xs opacity-60 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function Bdg({ children, color }) {
  const cls = {
    blue:   'bg-blue-100 text-blue-700',
    green:  'bg-green-100 text-green-700',
    purple: 'bg-purple-100 text-purple-700',
    gray:   'bg-gray-100 text-gray-600',
    orange: 'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls[color] || cls.gray}`}>{children}</span>
  );
}

function exportCSV(records) {
  const headers = ['Date', 'Merchant URL', 'Scan Type', 'Upload Type', 'Merchants', 'Amount (INR)', 'Period', 'Status', 'Tenant'];
  const rows = records.map(r => [
    r.run_at ? new Date(r.run_at).toLocaleString() : '',
    r.merchant_url || '',
    r.scan_type || '',
    r.upload_type || '',
    r.merchant_count || 1,
    r.amount_inr || 0,
    r.period_tag || '',
    r.status || '',
    r.tenant_name || '',
  ]);
  const csv = [headers, ...rows].map(row => row.map(c => `"${c}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `billing-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
}

const periodCardDefs = [
  { key: 'today',   label: 'Today',       icon: Calendar },
  { key: 'week',    label: 'This Week',   icon: BarChart3 },
  { key: 'month',   label: 'This Month',  icon: TrendingUp },
  { key: 'quarter', label: 'Quarter',     icon: ShoppingCart },
  { key: 'year',    label: 'This Year',   icon: Shield },
  { key: 'all',     label: 'All Time',    icon: CreditCard },
];

export default function BillingPage() {
  const isAdmin = isPlatformAdmin();
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [periods, setPeriods] = useState({});
  const [byTenant, setByTenant] = useState([]);
  const [tenantList, setTenantList] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [error, setError] = useState(null);
  const [filterPeriod, setFilterPeriod] = useState('');
  const [filterScanType, setFilterScanType] = useState('');
  const [filterTenant, setFilterTenant] = useState('');
  const [activePeriodCard, setActivePeriodCard] = useState('month');

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (filterPeriod) params.period = filterPeriod;
      if (filterScanType) params.scan_type = filterScanType;
      if (filterTenant) params.tenant_id = filterTenant;
      let [recRes, sumRes] = await Promise.all([
        authAPI.listBilling(params),
        authAPI.billingSummary(filterTenant ? { tenant_id: filterTenant } : {}),
      ]);
      const periodAll = sumRes.data.periods?.all || {};
      if ((periodAll.total_merchants || 0) === 0 && (recRes.data.count || 0) === 0) {
        try {
          await authAPI.billingSync(filterTenant ? { tenant_id: filterTenant } : {});
          [recRes, sumRes] = await Promise.all([
            authAPI.listBilling(params),
            authAPI.billingSummary(filterTenant ? { tenant_id: filterTenant } : {}),
          ]);
        } catch (_) { /* sync optional */ }
      }
      setRecords(recRes.data.records || []);
      setTotals(recRes.data.totals || {});
      setSummary(sumRes.data.summary || []);
      setPeriods(sumRes.data.periods || {});
      setByTenant(sumRes.data.by_tenant || []);
      if (isAdmin) {
        try { const tRes = await authAPI.billingTenants(); setTenantList(tRes.data.tenants || []); } catch (_) {}
      }
    } catch { setError('Failed to load billing data. Please try again.'); }
    finally { setLoading(false); }
  }, [filterPeriod, filterScanType, filterTenant, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const activePeriod = periods[activePeriodCard] || {};

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
        <p className="text-gray-500 text-sm">Loading billing data…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <CreditCard className="w-7 h-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
            <p className="text-sm text-gray-500">Merchant scan usage &amp; cost tracking</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadData}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={() => exportCSV(records)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{error}</div>}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'records',  label: `Scan Records${records.length ? ` (${records.length})` : ''}` },
          { key: 'summary',  label: 'Weekly Summary' },
          ...(isAdmin ? [{ key: 'tenants', label: 'By Tenant' }] : []),
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>{t.label}</button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <select value={filterPeriod} onChange={e => setFilterPeriod(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={filterScanType} onChange={e => setFilterScanType(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          {SCAN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {isAdmin && tenantList.length > 0 && (
          <select value={filterTenant} onChange={e => setFilterTenant(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Tenants</option>
            {tenantList.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </div>

      {/* ── OVERVIEW ─────────────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div className="space-y-6">
          {/* Period selector pills */}
          <div className="flex flex-wrap gap-2">
            {periodCardDefs.map(({ key, label }) => (
              <button key={key} onClick={() => setActivePeriodCard(key)}
                className={`px-4 py-1.5 text-sm rounded-full font-medium border transition-colors ${
                  activePeriodCard === key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                }`}>{label}</button>
            ))}
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
            <StatCard icon={CreditCard}   label="Total Amount"     value={fmtINR(activePeriod.total_inr)}      sub={`${fmt(activePeriod.total_merchants)} merchants`} color="blue" />
            <StatCard icon={BarChart3}    label="Onboarding Scans" value={fmt(activePeriod.onboarding)}        sub="Classification"  color="green" />
            <StatCard icon={Shield}       label="Monitoring Scans" value={fmt(activePeriod.monitoring)}        sub="Ongoing checks"  color="purple" />
            <StatCard icon={ShoppingCart} label="Single Uploads"   value={fmt(activePeriod.single_uploads)}    sub="Manual / API"    color="teal" />
            <StatCard icon={Users}        label="Bulk Uploads"     value={fmt(activePeriod.bulk_uploads)}      sub="Batch CSV/Excel" color="orange" />
          </div>

          {/* Period comparison mini-cards */}
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Period Comparison</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              {periodCardDefs.map(({ key, label, icon: Icon }) => {
                const p = periods[key] || {};
                return (
                  <div key={key} onClick={() => setActivePeriodCard(key)}
                    className={`rounded-xl border p-4 cursor-pointer transition-all ${
                      activePeriodCard === key
                        ? 'border-blue-400 bg-blue-50 shadow-sm' : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-gray-50'
                    }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-blue-500" />
                      <span className="text-xs font-medium text-gray-500">{label}</span>
                    </div>
                    <div className="text-lg font-bold text-gray-900">{fmtINR(p.total_inr)}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{fmt(p.total_merchants)} merchants</div>
                    <div className="mt-2 grid grid-cols-2 gap-1 text-xs text-gray-500">
                      <span>↑ {fmt(p.onboarding)} OB</span>
                      <span>↺ {fmt(p.monitoring)} MO</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pricing reference */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-800 mb-3">Pricing Reference (INR)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Onboarding — Single', price: '₹50.00' },
                { label: 'Onboarding — Bulk',   price: '₹40.00' },
                { label: 'Monitoring — Single',  price: '₹25.00' },
                { label: 'Monitoring — Bulk',    price: '₹20.00' },
              ].map(item => (
                <div key={item.label} className="bg-white rounded-lg border border-gray-200 p-3">
                  <div className="text-xs text-gray-500 mb-1">{item.label}</div>
                  <div className="text-xl font-bold text-blue-700">{item.price}</div>
                  <div className="text-xs text-gray-400">per merchant URL</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SCAN RECORDS ─────────────────────────────────────────────────────── */}
      {tab === 'records' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {totals.total_merchants > 0 && (
            <div className="flex flex-wrap gap-6 px-5 py-3 bg-blue-50 border-b border-blue-100 text-sm">
              <span className="text-blue-800 font-medium">{fmt(totals.total_merchants)} merchants · {fmtINR(totals.total_inr)}</span>
              <span className="text-blue-600">Onboarding: {fmt(totals.onboarding_count)}</span>
              <span className="text-blue-600">Monitoring: {fmt(totals.monitoring_count)}</span>
              <span className="text-blue-600">Single: {fmt(totals.single_count)}</span>
              <span className="text-blue-600">Bulk: {fmt(totals.bulk_count)}</span>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left border-b border-gray-200">
                  <th className="px-4 py-3 font-medium text-gray-600">Merchant</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Type</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Upload</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-right">Count</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-right">Amount (₹)</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Period</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Date &amp; Time</th>
                  {isAdmin && <th className="px-4 py-3 font-medium text-gray-600">Tenant</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      {r.merchant_name && <div className="font-medium text-gray-900 text-xs">{r.merchant_name}</div>}
                      <div className="text-xs text-gray-400 truncate max-w-[200px]">{r.merchant_url || '—'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Bdg color={r.scan_type === 'classification' ? 'blue' : 'purple'}>
                        {r.scan_type === 'classification' ? 'Onboarding' : 'Monitoring'}
                      </Bdg>
                    </td>
                    <td className="px-4 py-3">
                      <Bdg color={r.upload_type === 'bulk' ? 'orange' : 'gray'}>
                        {r.upload_type === 'bulk' ? '📦 Bulk' : '🔗 Single'}
                      </Bdg>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmt(r.merchant_count)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-green-700">{fmtINR(r.amount_inr)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        r.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs font-mono">{r.period_tag}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {r.run_at ? new Date(r.run_at).toLocaleString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      }) : '—'}
                    </td>
                    {isAdmin && <td className="px-4 py-3 text-gray-500 text-xs">{r.tenant_name || '—'}</td>}
                  </tr>
                ))}
                {records.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 9 : 8} className="text-center py-16 text-gray-400">
                      <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p>No billing records found</p>
                      <p className="text-xs mt-1">Records are created automatically when merchants are classified</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── WEEKLY SUMMARY ────────────────────────────────────────────────────── */}
      {tab === 'summary' && (
        <div className="space-y-4">
          {summary.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>No weekly summary data found</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {summary.map(s => (
                <div key={s.period_tag} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-sm transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-lg font-bold text-gray-900 font-mono">{s.period_tag}</span>
                      <span className="ml-2 text-xs text-gray-400">{s.scan_count} sessions</span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-700">{fmtINR(s.total_inr)}</div>
                      <div className="text-xs text-gray-400">{fmt(s.total_merchants)} merchants</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-blue-50 rounded-lg p-2.5">
                      <div className="text-xs text-blue-500 font-medium mb-1">Onboarding · Single</div>
                      <div className="font-bold text-blue-900">{fmt(s.classification_single)}</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2.5">
                      <div className="text-xs text-blue-500 font-medium mb-1">Onboarding · Bulk</div>
                      <div className="font-bold text-blue-900">{fmt(s.classification_bulk)}</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-2.5">
                      <div className="text-xs text-purple-500 font-medium mb-1">Monitoring · Single</div>
                      <div className="font-bold text-purple-900">{fmt(s.monitoring_single)}</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-2.5">
                      <div className="text-xs text-purple-500 font-medium mb-1">Monitoring · Bulk</div>
                      <div className="font-bold text-purple-900">{fmt(s.monitoring_bulk)}</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    <span>{fmt(s.total_batches || 0)} batch(es)</span>
                    <span className="font-semibold text-blue-700 text-sm">{fmtINR(s.total_inr)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── BY TENANT (admin only) ────────────────────────────────────────────── */}
      {tab === 'tenants' && isAdmin && (
        <div className="space-y-4">
          {byTenant.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>No tenant billing data</p>
            </div>
          ) : (
            <>
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl p-5">
                <div className="text-sm opacity-80 mb-1">Total Revenue — All Tenants</div>
                <div className="text-3xl font-bold">
                  {fmtINR(byTenant.reduce((s, r) => s + Number(r.total_inr || 0), 0))}
                </div>
                <div className="text-sm opacity-70 mt-1">
                  {fmt(byTenant.reduce((s, r) => s + Number(r.total_merchants || 0), 0))} merchants across {byTenant.length} tenants
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left border-b border-gray-200">
                      <th className="px-5 py-3 font-medium text-gray-600">Tenant</th>
                      <th className="px-5 py-3 font-medium text-gray-600 text-right">Total Merchants</th>
                      <th className="px-5 py-3 font-medium text-gray-600 text-right">Onboarding</th>
                      <th className="px-5 py-3 font-medium text-gray-600 text-right">Monitoring</th>
                      <th className="px-5 py-3 font-medium text-gray-600 text-right">Amount (₹)</th>
                      <th className="px-5 py-3 font-medium text-gray-600 text-right">% Share</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {byTenant.map((t, i) => {
                      const grandTotal = byTenant.reduce((s, r) => s + Number(r.total_inr || 0), 0);
                      const pct = grandTotal > 0 ? ((Number(t.total_inr || 0) / grandTotal) * 100).toFixed(1) : 0;
                      return (
                        <tr key={t.tenant_id || i} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                {(t.tenant_name || 'U')[0].toUpperCase()}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{t.tenant_name || '—'}</div>
                                <div className="text-xs text-gray-400">ID: {t.tenant_id || '—'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-right font-semibold">{fmt(t.total_merchants)}</td>
                          <td className="px-5 py-3 text-right text-blue-700">{fmt(t.onboarding)}</td>
                          <td className="px-5 py-3 text-right text-purple-700">{fmt(t.monitoring)}</td>
                          <td className="px-5 py-3 text-right font-bold text-green-700">{fmtINR(t.total_inr)}</td>
                          <td className="px-5 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-20 bg-gray-200 rounded-full h-1.5">
                                <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-xs text-gray-500 w-10 text-right">{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
